import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { ShieldAlert, KeyRound, Lock, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Forced first-login flow — user MUST change password AND PIN to leave this screen.
// Both must pass server-side complexity rules. Gate is enforced by checking
// /api/auth/security-status on every protected route mount (see ProtectedRoute).
const FirstLoginReset = () => {
  const { api, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Step 1 — password
  const [oldPwd, setOldPwd] = useState("kinntegra123");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);

  // Step 2 — pin
  const [oldPin, setOldPin] = useState("1234");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pinSaving, setPinSaving] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/auth/security-status");
      setStatus(data);
      if (!data.force_password_reset && !data.force_pin_reset) {
        toast.success("Setup complete! Welcome aboard.");
        if (refreshUser) await refreshUser();
        navigate("/dashboard", { replace: true });
      }
    } catch (e) {
      toast.error("Failed to load security status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const validPwd = (p) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/.test(p);

  const handlePwdSubmit = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) { toast.error("Passwords don't match"); return; }
    if (!validPwd(newPwd)) {
      toast.error("Password must be 8+ chars with upper, lower, number and special character");
      return;
    }
    if (newPwd === oldPwd) { toast.error("New password cannot match the old one"); return; }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success("Password updated");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setPwdSaving(false); }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (newPin !== confirmPin) { toast.error("PINs don't match"); return; }
    if (!/^\d{4}$/.test(newPin)) { toast.error("PIN must be 4 digits"); return; }
    if (newPin === "1234") { toast.error("PIN cannot be the default 1234"); return; }
    if (newPin === oldPin) { toast.error("New PIN cannot match the old one"); return; }
    setPinSaving(true);
    try {
      await api.post("/auth/change-pin", { old_pin: oldPin, new_pin: newPin });
      toast.success("PIN updated");
      await reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setPinSaving(false); }
  };

  if (loading || !status) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>;
  }

  const stepPwd = status.force_password_reset;
  const stepPin = !stepPwd && status.force_pin_reset;

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-4" style={{ backgroundColor: "#07111F", paddingTop: "max(1rem, env(safe-area-inset-top))", paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }} data-testid="first-login-reset">
      <Card className="w-full max-w-lg shadow-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-3">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
              <ShieldAlert className="w-8 h-8 text-amber-600" />
            </div>
          </div>
          <CardTitle className="text-2xl">First-Login Security Setup</CardTitle>
          <CardDescription>
            For your protection, you must change your default password AND PIN before accessing the platform.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Stepper */}
          <div className="flex items-center justify-between">
            <StepDot active={stepPwd} done={!status.force_password_reset} label="Password" icon={Lock} />
            <div className={`flex-1 h-0.5 mx-2 ${!status.force_password_reset ? "bg-emerald-400" : "bg-gray-200"}`} />
            <StepDot active={stepPin} done={!status.force_pin_reset} label="PIN" icon={KeyRound} />
          </div>

          {stepPwd && (
            <form onSubmit={handlePwdSubmit} className="space-y-3" data-testid="reset-password-form">
              <div className="space-y-1">
                <Label className="text-xs">Current Password</Label>
                <Input type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} data-testid="old-password" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">New Password</Label>
                <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="new-password" />
                <p className="text-[11px] text-gray-500">8+ chars · upper · lower · number · special</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Confirm Password</Label>
                <Input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} data-testid="confirm-password" />
              </div>
              <Button type="submit" disabled={pwdSaving} className="w-full" style={{ backgroundColor: "#1a3f3a" }} data-testid="submit-password">
                {pwdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                Set Password
              </Button>
            </form>
          )}

          {stepPin && (
            <form onSubmit={handlePinSubmit} className="space-y-3" data-testid="reset-pin-form">
              <div className="space-y-1">
                <Label className="text-xs">Current PIN</Label>
                <Input type="password" inputMode="numeric" maxLength={4} value={oldPin} onChange={(e) => setOldPin(e.target.value.replace(/\D/g, ""))} data-testid="old-pin" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">New PIN (4 digits, not 1234)</Label>
                <Input type="password" inputMode="numeric" maxLength={4} value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} data-testid="new-pin" className="text-center text-2xl tracking-widest" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Confirm PIN</Label>
                <Input type="password" inputMode="numeric" maxLength={4} value={confirmPin} onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))} data-testid="confirm-pin" className="text-center text-2xl tracking-widest" />
              </div>
              <Button type="submit" disabled={pinSaving} className="w-full" style={{ backgroundColor: "#1a3f3a" }} data-testid="submit-pin">
                {pinSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                Set PIN
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

function StepDot({ active, done, label, icon: Icon }) {
  return (
    <div className="flex flex-col items-center">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
        done ? "bg-emerald-500 text-white" : active ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-400"
      }`}>
        {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
      </div>
      <span className={`text-xs mt-1 ${active || done ? "font-semibold text-gray-800" : "text-gray-400"}`}>{label}</span>
    </div>
  );
}

export default FirstLoginReset;
